import axios from 'axios';
import { AuditDetails } from '../../audit/auditApi';
import { apiErrorMessage } from '../edit/editApi';

/** Базовый адрес истории изменений (все запросы проверяются сервером по сессии и правам reports.history.*) */
export const HISTORY_API = '/api/v2/reports/history';

/** Экран отчётности, у которого есть кнопка «История» (совпадает с разделом записей журнала) */
export type HistoryScreen = 'edit_day' | 'edit_month' | 'math' | 'delivery';

export const HISTORY_SCREEN_TITLES: Record<HistoryScreen, string> = {
  edit_day: 'Редактирование',
  edit_month: 'Ред. мес. отчет',
  math: 'Мат. отчет',
  delivery: 'Сверка доставок'
};

export const HISTORY_PAGE_SIZE = 30;

/** Запись истории. Сводка (summary) и признак подробностей приходят только при праве reports.history.details */
export interface HistoryRow {
  id: number;
  createdAt: string;
  username: string | null;
  fullName: string | null;
  roleName: string | null;
  actorUsername: string | null;
  section: string | null;
  sectionTitle: string | null;
  action: string;
  actionTitle: string;
  kind: string;
  kindTitle: string;
  entityLabel: string | null;
  pharmacyNum: string | null;
  pharmacyUsername: string | null;
  period: string | null;
  summary: string | null;
  hasDetails: boolean;
}

/** Подробности записи: изменённые поля, снимок удалённых данных и счётчики (тот же формат, что у журнала действий) */
export type HistoryDetails = AuditDetails;

export interface HistoryRecord extends HistoryRow {
  details: HistoryDetails | null;
}

/** Контекст экрана: пустые значения означают «без ограничения» */
export interface HistoryContext {
  screen: HistoryScreen;
  /** Логин аптеки (users.username) */
  pharmacy?: string | null;
  /** Месяц YYYY-MM или день YYYY-MM-DD */
  period?: string | null;
}

export interface HistoryListResponse {
  rows: HistoryRow[];
  total: number;
  page: number;
  pages: number;
  canDetails: boolean;
}

interface ApiEnvelope {
  success?: boolean;
  message?: string;
}

type ListDto = ApiEnvelope & Partial<HistoryListResponse>;
type DetailDto = ApiEnvelope & { record?: HistoryRecord };

/** Текст ошибки истории для пользователя (сообщение сервера или запасной текст) */
export function historyErrorMessage(error: unknown, fallback: string): string {
  return apiErrorMessage(error, fallback);
}

function ensureSuccess<T extends ApiEnvelope>(data: T | undefined, fallback: string): T {
  if (!data || !data.success) throw new Error((data && data.message) || fallback);
  return data;
}

/** Параметры запроса: пустые условия не отправляются */
function toParams(context: HistoryContext, page: number): Record<string, string | number> {
  const params: Record<string, string | number> = { screen: context.screen, page, pageSize: HISTORY_PAGE_SIZE };
  if (context.pharmacy) params.pharmacy = context.pharmacy;
  if (context.period) params.period = context.period;
  return params;
}

export async function fetchHistory(context: HistoryContext, page: number): Promise<HistoryListResponse> {
  const res = await axios.get<ListDto>(HISTORY_API, { params: toParams(context, page) });
  const data = ensureSuccess(res.data, 'Не удалось загрузить историю');
  return {
    rows: data.rows || [],
    total: data.total || 0,
    page: data.page || page,
    pages: data.pages || 1,
    canDetails: Boolean(data.canDetails)
  };
}

export async function fetchHistoryRecord(id: number): Promise<HistoryRecord> {
  const res = await axios.get<DetailDto>(`${HISTORY_API}/${id}`);
  const data = ensureSuccess(res.data, 'Не удалось загрузить подробности');
  if (!data.record) throw new Error('Запись истории не найдена');
  return data.record;
}
